import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Copy, Check, AlertCircle } from 'lucide-react';
import { linkDiscordServer, manageWhitelist } from '../../hooks/useDiscordServers';
import { supabase } from '../../supabase';

interface DiscordBridgeAdminModalProps {
  isOpen: boolean;
  onClose: () => void;
  vaults: any[];
  users: any[];
}

export default function DiscordBridgeAdminModal({
  isOpen,
  onClose,
  vaults,
  users,
}: DiscordBridgeAdminModalProps) {
  const [linkedServers, setLinkedServers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  // Form states for linking new server
  const [discordGuildId, setDiscordGuildId] = useState('');
  const [discordGuildName, setDiscordGuildName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Whitelist management
  const [selectedGuildId, setSelectedGuildId] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [whitelistAction, setWhitelistAction] = useState<'add' | 'remove'>('add');

  // Confirmation dialog
  const [confirmReplace, setConfirmReplace] = useState(false);
  const [pendingLink, setPendingLink] = useState<{ guildId: string; guildName: string } | null>(null);

  // Fetch linked servers on mount
  useEffect(() => {
    if (isOpen) {
      fetchLinkedServers();
    }
  }, [isOpen]);

  const fetchLinkedServers = async () => {
    try {
      setLoading(true);
      const session = await supabase.auth.getSession();
      const token = session?.data?.session?.access_token;

      if (!token) {
        setError('Not authenticated');
        setLinkedServers([]);
        setLoading(false);
        return;
      }

      const response = await fetch('https://psydechatthing.onrender.com/api/discord/servers', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const data: any = await response.json().catch(() => ({ error: 'Failed to fetch' }));
        setError(data.error || 'Failed to fetch linked servers');
        setLinkedServers([]);
        return;
      }

      const data = await response.json() as { servers?: any[] };
      setLinkedServers(data.servers || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching linked servers:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch linked servers');
      setLinkedServers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLinkServer = async (e: React.FormEvent, confirmReplace?: boolean) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!discordGuildId || !discordGuildName) {
      setError('Discord Guild ID and Name are required');
      return;
    }

    try {
      setLoading(true);
      const session = await supabase.auth.getSession();
      const token = session?.data?.session?.access_token;

      if (!token) {
        setError('Not authenticated');
        return;
      }

      const response = await fetch('https://psydechatthing.onrender.com/api/discord/link-server', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          discord_guild_id: discordGuildId,
          discord_guild_name: discordGuildName,
          discord_guild_icon: null,
          confirm_replace: confirmReplace || false,
        }),
      });

      if (response.status === 409) {
        // Server already linked - show confirmation
        const data = await response.json();
        setPendingLink({ guildId: discordGuildId, guildName: discordGuildName });
        setConfirmReplace(true);
        setError(null);
        return;
      }

      if (!response.ok) {
        const data: any = await response.json().catch(() => ({ error: 'Failed to link' }));
        setError(data.error || 'Failed to link server');
        return;
      }

      setSuccess(`✅ Linked Discord server "${discordGuildName}" successfully!`);
      setDiscordGuildId('');
      setDiscordGuildName('');
      setConfirmReplace(false);
      setPendingLink(null);
      fetchLinkedServers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to link server');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReplace = async () => {
    if (!pendingLink) return;
    setConfirmReplace(false);

    const formEvent = new Event('submit') as unknown as React.FormEvent;
    await handleLinkServer(formEvent, true);
    setPendingLink(null);
  };

  const handleWhitelist = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!selectedGuildId || !selectedUserId) {
      setError('Please select both a Discord server and user');
      return;
    }

    try {
      setLoading(true);
      await manageWhitelist(selectedGuildId, selectedUserId, whitelistAction);
      setSuccess(`✅ ${whitelistAction === 'add' ? 'Added' : 'Removed'} user from whitelist!`);
      fetchLinkedServers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to manage whitelist');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 2000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-slate-900 border border-white/10 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-slate-900 border-b border-white/10 p-6 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <span className="text-indigo-400">⚡</span> Discord Bridge Admin
          </h2>
          <button
            onClick={onClose}
            className="text-white/50 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-8">
          {/* Alerts */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="text-red-400 flex-shrink-0 mt-0.5" size={20} />
              <p className="text-red-200 text-sm">{error}</p>
            </div>
          )}
          {success && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 flex items-start gap-3">
              <Check className="text-green-400 flex-shrink-0 mt-0.5" size={20} />
              <p className="text-green-200 text-sm">{success}</p>
            </div>
          )}

          {/* Section 1: Link Discord Server */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">Link Discord Server</h3>
            <form onSubmit={handleLinkServer} className="space-y-4">
              <div>
                <label className="block text-sm text-white/70 mb-2">Discord Guild ID</label>
                <input
                  type="text"
                  value={discordGuildId}
                  onChange={(e) => setDiscordGuildId(e.target.value)}
                  placeholder="e.g., 1502532461920260136"
                  className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-2 text-white placeholder-white/30 focus:border-indigo-500 focus:outline-none"
                />
                <p className="text-xs text-white/50 mt-1">
                  Find in Discord: Enable Developer Mode → Right-click server → Copy Server ID
                </p>
              </div>

              <div>
                <label className="block text-sm text-white/70 mb-2">Discord Server Name</label>
                <input
                  type="text"
                  value={discordGuildName}
                  onChange={(e) => setDiscordGuildName(e.target.value)}
                  placeholder="e.g., Cool Guys"
                  className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-2 text-white placeholder-white/30 focus:border-indigo-500 focus:outline-none"
                />
              </div>

              <p className="text-xs text-white/50">A new vault will be automatically created for this Discord server.</p>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-600/50 text-white font-semibold py-2 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <Plus size={18} />
                Link Discord Server
              </button>
            </form>
          </div>

          {/* Section 2: Manage Whitelist */}
          <div className="space-y-4 border-t border-white/10 pt-6">
            <h3 className="text-lg font-semibold text-white">Manage Whitelist</h3>
            {linkedServers.length === 0 ? (
              <p className="text-white/50 text-sm">No Discord servers linked yet.</p>
            ) : (
              <form onSubmit={handleWhitelist} className="space-y-4">
                <div>
                  <label className="block text-sm text-white/70 mb-2">Discord Server</label>
                  <select
                    value={selectedGuildId}
                    onChange={(e) => setSelectedGuildId(e.target.value)}
                    className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-2 text-white focus:border-indigo-500 focus:outline-none"
                  >
                    <option value="">Select a Discord server...</option>
                    {linkedServers.map((server) => (
                      <option key={server.discord_guild_id} value={server.discord_guild_id}>
                        {server.discord_guild_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-white/70 mb-2">PSYDEChat User</label>
                  <select
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-2 text-white focus:border-indigo-500 focus:outline-none"
                  >
                    <option value="">Select a user...</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name || user.email}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setWhitelistAction('add')}
                    className={`flex-1 py-2 rounded-lg font-semibold transition-colors ${
                      whitelistAction === 'add'
                        ? 'bg-green-600 text-white'
                        : 'bg-black/50 border border-white/10 text-white/50 hover:text-white'
                    }`}
                  >
                    Add to Whitelist
                  </button>
                  <button
                    type="button"
                    onClick={() => setWhitelistAction('remove')}
                    className={`flex-1 py-2 rounded-lg font-semibold transition-colors ${
                      whitelistAction === 'remove'
                        ? 'bg-red-600 text-white'
                        : 'bg-black/50 border border-white/10 text-white/50 hover:text-white'
                    }`}
                  >
                    Remove from Whitelist
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-600/50 text-white font-semibold py-2 rounded-lg transition-colors"
                >
                  {whitelistAction === 'add' ? 'Add User' : 'Remove User'}
                </button>
              </form>
            )}
          </div>

          {/* Section 3: Linked Servers */}
          <div className="space-y-4 border-t border-white/10 pt-6">
            <h3 className="text-lg font-semibold text-white">Linked Discord Servers</h3>
            {loading && linkedServers.length === 0 ? (
              <p className="text-white/50">Loading...</p>
            ) : linkedServers.length === 0 ? (
              <p className="text-white/50 text-sm">No servers linked yet.</p>
            ) : (
              <div className="space-y-3">
                {linkedServers.map((server) => (
                  <div
                    key={server.discord_guild_id}
                    className="bg-black/50 border border-white/10 rounded-lg p-4 space-y-2"
                  >
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-white">{server.discord_guild_name}</h4>
                      <button
                        onClick={() => copyToClipboard(server.discord_guild_id, 'guild')}
                        className="text-white/50 hover:text-white transition-colors"
                        title="Copy Guild ID"
                      >
                        {copied === 'guild' ? (
                          <Check size={18} className="text-green-400" />
                        ) : (
                          <Copy size={18} />
                        )}
                      </button>
                    </div>
                    <p className="text-xs text-white/50">ID: {server.discord_guild_id}</p>
                    <p className="text-xs text-white/50">
                      Linked to: <span className="text-white/70">{server.vaults?.name || 'Unknown'}</span>
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ID Lookup Helper */}
          <div className="space-y-4 border-t border-white/10 pt-6">
            <h3 className="text-lg font-semibold text-white">Quick ID Reference</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-white/70">Vaults</h4>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {vaults.slice(0, 5).map((vault) => (
                    <div
                      key={vault.id}
                      className="bg-black/30 rounded px-3 py-2 flex items-center justify-between group hover:bg-black/50 transition-colors"
                    >
                      <span className="text-xs text-white/70">{vault.name}</span>
                      <button
                        onClick={() => copyToClipboard(vault.id, `vault-${vault.id}`)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        {copied === `vault-${vault.id}` ? (
                          <Check size={14} className="text-green-400" />
                        ) : (
                          <Copy size={14} className="text-white/50" />
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-white/70">Users</h4>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {users.slice(0, 5).map((user) => (
                    <div
                      key={user.id}
                      className="bg-black/30 rounded px-3 py-2 flex items-center justify-between group hover:bg-black/50 transition-colors"
                    >
                      <span className="text-xs text-white/70">{user.name || user.email}</span>
                      <button
                        onClick={() => copyToClipboard(user.id, `user-${user.id}`)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        {copied === `user-${user.id}` ? (
                          <Check size={14} className="text-green-400" />
                        ) : (
                          <Copy size={14} className="text-white/50" />
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {confirmReplace && pendingLink && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="bg-slate-900 border border-amber-500/30 rounded-xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <AlertCircle className="text-amber-400" size={24} />
              Replace Existing Link?
            </h3>
            <p className="text-white/80">
              This Discord server is <span className="font-bold text-amber-300">already linked</span>. Confirming will:
            </p>
            <ul className="space-y-2 ml-4">
              <li className="text-sm text-white/70">❌ Delete the old link</li>
              <li className="text-sm text-white/70">✅ Create a new vault for "{pendingLink.guildName}"</li>
            </ul>
            <p className="text-xs text-white/50 italic">The old vault will be left inactive.</p>
            <div className="flex gap-3 pt-4">
              <button
                onClick={() => {
                  setConfirmReplace(false);
                  setPendingLink(null);
                }}
                className="flex-1 py-2 rounded-lg bg-white/10 border border-white/20 text-white font-semibold hover:bg-white/20 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmReplace}
                disabled={loading}
                className="flex-1 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 disabled:bg-amber-600/50 text-white font-semibold transition-colors"
              >
                {loading ? 'Linking...' : 'Yes, Replace It'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
